import { supabase } from './supabase'
import { v4 as uuidv4 } from 'uuid'
import { hasAnalyticsConsent } from '../components/ui/CookieConsent'

class AnalyticsService {
  private sessionId: string
  private userEmail: string | null = null
  private sessionStartTime: number
  private pageViews: number = 0
  private inputDebounceTimer: number | undefined

  constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.sessionStartTime = Date.now()
    this.userEmail = null
    this.initializeSession()
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('analytics_session_id')
    if (!sessionId) {
      sessionId = uuidv4()
      sessionStorage.setItem('analytics_session_id', sessionId)
    }
    return sessionId
  }

  private async initializeSession() {
    this.pageViews = 1
    // Session data will be saved in one INSERT when user leaves (beforeunload)
  }

  private getDeviceType(userAgent: string): string {
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet'
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) return 'mobile'
    return 'desktop'
  }

  private getBrowser(userAgent: string): string {
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Other'
  }

  // User Management
  async createUser(
    email: string,
    source: string = 'hero',
    options?: { name?: string; company?: string }
  ): Promise<{ success: boolean; isDuplicate: boolean }> {
    try {
      const { error } = await supabase
        .from('users')
        .insert({
          email,
          source,
          data: options && (options.name || options.company)
            ? { name: options.name, company: options.company }
            : undefined
        })

      if (error) {
        // Check if it's a duplicate key error
        if (error.message && error.message.includes('duplicate key value')) {
          console.log('User already exists:', email)
          // Still store email since they're already in the system
          sessionStorage.setItem('userEmail', email)
          this.userEmail = email
          // Insert-only workaround: log enrichment in user_interactions
          if (options && (options.name || options.company)) {
            try {
              const cookiesRequired = false
              await this.trackInteraction(
                'profile_enrichment',
                'user_profile',
                { name: options.name, company: options.company },
                cookiesRequired
              )
            } catch (enrichmentErr) {
              console.warn('Could not insert profile enrichment interaction:', enrichmentErr)
            }
          }
          return { success: true, isDuplicate: true }
        }
        
        console.error('Error creating user:', error)
        return { success: false, isDuplicate: false }
      }

      // Store email in session storage and instance for tracking
      sessionStorage.setItem('userEmail', email)
      this.userEmail = email
      
      return { success: true, isDuplicate: false }
    } catch (error) {
      console.error('Error creating user:', error)
      return { success: false, isDuplicate: false }
    }
  }

  async trackInteraction(eventType: string, element?: string, data?: any, cookiesRequired: boolean = true) {
    if (cookiesRequired && !hasAnalyticsConsent()) {
      return
    }
    
    try {
      const knownUserId = this.userEmail || null
      const typedEmail = sessionStorage.getItem('userEmail') || null
      const payloadData = {
        ...((data || {})),
        ...(typedEmail ? { email: typedEmail } : {})
      }
      await supabase
        .from('user_interactions')
        .insert({
          user_id: knownUserId,
          session_id: this.sessionId,
          event_type: eventType,
          page: window.location.pathname,
          element,
          data: payloadData,
        })
    } catch (error) {
      console.error('Error tracking interaction:', error)
    }
  }

  // Survey Tracking - Individual Question Response
  async trackSurveyResponse(questionId: string, questionText: string, answer: string, completed: boolean = false) {
    
    try {
      await supabase
        .from('survey_responses')
        .insert({
          user_id: this.userEmail || null,
          question_id: questionId,
          question_text: questionText,
          answer,
          completed,
        })
      
      console.log(`Survey response saved for ${this.userEmail}: ${questionId} = ${answer}`);
    } catch (error) {
      console.error('Error tracking survey response:', error)
    }
  }

  // Page View Tracking
  async trackPageView() {
    this.pageViews += 1
    // Page view count tracked locally, saved to DB when session ends
    await this.trackInteraction('page_view')
  }

  // Form Tracking
  async trackFormStart(formName: string) {
    await this.trackInteraction('form_start', formName)
  }

  async trackFormSubmit(formName: string, data?: any) {
    await this.trackInteraction('form_submit', formName, data)
  }

  async trackFormAbandonment(formName: string, fieldName: string) {
    await this.trackInteraction('form_abandon', formName, { field: fieldName })
  }

  // Input Tracking - per keystroke
  async trackInputChange(element: string, text: string) {
    // Debounce input events to avoid excessive writes
    if (this.inputDebounceTimer) {
      clearTimeout(this.inputDebounceTimer)
    }
    this.inputDebounceTimer = window.setTimeout(() => {
      this.trackInteraction('input_change', element, { text })
    }, 150)
  }

  // Button/CTA Tracking
  async trackButtonClick(buttonName: string, location?: string) {
    await this.trackInteraction('button_click', buttonName, { location })
  }

  // Scroll Tracking
  async trackScroll(percentage: number) {
    if (percentage === 25 || percentage === 50 || percentage === 75 || percentage === 100) {
      await this.trackInteraction('scroll', `${percentage}%`)
    }
  }

  async endSession() {
    if (!hasAnalyticsConsent()) {
      return
    }
    
    try {
      const userAgent = navigator.userAgent
      const deviceType = this.getDeviceType(userAgent)
      const browser = this.getBrowser(userAgent)
      const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000)

      // Insert final session data when user leaves
      await supabase
        .from('user_sessions')
        .insert({
          session_id: this.sessionId,
          // Only set user_id when we know the user exists in DB
          user_id: this.userEmail || null,
          page_views: this.pageViews,
          device_type: deviceType,
          browser: browser,
          duration_seconds: duration
        })
      
      console.log('Session ended:', { 
        duration, 
        page_views: this.pageViews, 
        device_type: deviceType 
      })
    } catch (error) {
      console.error('Error saving session data:', error)
    }
  }

}

// Create singleton instance
export const analytics = new AnalyticsService()

// Setup scroll tracking
let scrollTimeout: NodeJS.Timeout
window.addEventListener('scroll', () => {
  clearTimeout(scrollTimeout)
  scrollTimeout = setTimeout(() => {
    const scrollPercentage = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    )
    analytics.trackScroll(scrollPercentage)
  }, 100)
})

// Setup page unload tracking
window.addEventListener('beforeunload', () => {
  analytics.endSession()
}) 